import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import { usePerms } from 'contexts/PermsContext';
import { GetRouteData } from 'data/routeData';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import useTextToSpeech from 'hooks/useTextToSpeech';
import Cookies from 'js-cookie';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { IconContext } from 'react-icons';
import { FaRegUserCircle } from 'react-icons/fa';
import { FaChevronDown, FaHouse, FaRegUser, FaUser } from 'react-icons/fa6';
import { FiExternalLink, FiLogOut } from 'react-icons/fi';
import { IoInformationCircle } from 'react-icons/io5';
import { PiPersonArmsSpreadFill, PiVideoFill, PiWrenchFill } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Popup from 'reactjs-popup';
import { logout } from 'services/auth/auth.service';
import { selectUsername } from 'store/selectors/User/userSelectors';
import { clearDisplayOptions } from 'store/slices/DisplayOptions/displayOptionsSlice';
import { resetStore } from 'store/store';
import { clearCookies } from 'utils/sessions/cookie.utils';
import styles from './assets/header.module.css';
import PlacesSelector from './PlacesSelector';

const USER_MENU_WIDTH = 'min(255px, calc(100vw - 2rem))';

const getEffectiveHeaderPath = (location) => {
  const pathname = location?.pathname || '';
  if (pathname !== '/system/globalactions') return pathname;

  const scope = location?.state?.alarmShareContext?.scope;
  if (scope === 'history') return '/overwatch/alarmhistory';
  if (scope === 'pending') return '/overwatch/pendingalarms';

  return pathname;
};

export default function Header({
  widgetRefresh,
  setWidgetRefresh,
  setShowFull,
  setNewTermsFullyAgreed,
  setTermsFullyViewed,
  setHasSignedIn,
  setHasFetchedPerms,
  setCookiesAgreed,
  setPrivacyAgreed,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const username = useSelector(selectUsername);
  const [routeData, setRouteData] = useState(null);
  const { instance } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const perms = usePerms();
  const tts = useTextToSpeech();
  const widthRef = useRef(window.innerWidth);
  const authContext = useContext(AuthContext);
  const { logout: authLogout } = authContext;

  const handleLogout = () => {
    if (isMsalAuthenticated) {
      instance.logoutRedirect({
        onRedirectNavigate: (url) => {
          return false;
        },
      });
    }
    setShowFull(true);

    // Call API logout
    logout();

    // Keep remember me data if set
    let keepCookies = [];
    if (Cookies.get('rememberMe') === 'true') {
      keepCookies = ['rememberMe', 'email'];
    }
    clearCookies(keepCookies);

    // Explicitly clear privacy and cookie agreement for legal compliance
    Cookies.remove('cookiesAgreed');
    Cookies.remove('privacyAgreed');
    setCookiesAgreed(false);
    setPrivacyAgreed(false);

    // Reset app state
    perms.reset();
    tts.cancel();
    setNewTermsFullyAgreed(false);
    setTermsFullyViewed(false);
    setHasSignedIn(false);
    setHasFetchedPerms(false);

    // Use centralized auth logout
    authLogout();
    dispatch(resetStore());
    dispatch(clearDisplayOptions());
    navigate('/');
  };

  useEffect(() => {
    window.addEventListener('resize', () => (widthRef.current = window.innerWidth));
  }, []);

  useEffect(() => {
    let data;
    const effectivePath = getEffectiveHeaderPath(location);

    if (effectivePath === '/dashboard') {
      data = { icon: () => <FaHouse />, displayName: 'Dashboard' };
    } else if (effectivePath === '/vms') {
      data = { icon: () => <PiVideoFill />, displayName: 'Overwatch VMS' };
    } else if (effectivePath === '/maintenancedashboard') {
      data = {
        icon: () => <PiWrenchFill />,
        displayName: 'Maintenance Devices',
      };
    } else {
      const sideBarData = GetRouteData(perms);

      sideBarData.forEach((section) => {
        section.options.forEach((option) => {
          if (option.href === effectivePath) data = { icon: option.icon, displayName: option.name };
        });
      });
    }

    setRouteData(data);
  }, [location.pathname, location.state, perms]);

  return (
    <Container
      as="header"
      fluid
      className="d-flex flex-column flex-xl-row align-items-center justify-content-between py-4 gap-3 w-100"
      style={{
        backgroundColor: 'var(--color-primary-shadow)',
        color: 'var(--color-font)',
      }}
    >
      {routeData ? (
        // Page Title and Icon
        <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
          <IconContext.Provider value={{ color: 'var(--color-font)', size: 45 }}>
            <div>{routeData.icon()}</div>
          </IconContext.Provider>
          <div className="gap-3 gap-sm-4 d-flex align-items-center justify-content-center">
            <div className="fs-1 text-center">{routeData.displayName}</div>
          </div>
        </div>
      ) : (
        <div></div>
      )}
      {/* Location Selector */}
      <div className="d-flex flex-column flex-xl-row align-items-center gap-3 me-3">
        {!location.pathname.includes('usermenu') && (
          <Button
            as="div"
            className={`d-flex me-3 justify-content-between align-items-center fs-6 ${styles['pill']}`}
          >
            <PlacesSelector widgetRefresh={widgetRefresh} setWidgetRefresh={setWidgetRefresh} />
          </Button>
        )}
        {/* Overwatch Community Link */}
        <Button
          onClick={() => window.open('http://www.overwatch.ai/', '_blank', 'noreferrer')}
          className={styles['pill']}
          variant="primary"
        >
          <div
            className="d-flex align-items-center justify-content-start fs-6"
            style={{
              width: 200,
              height: '100%',
            }}
          >
            <div>
              <div
                className={`d-flex align-items-center justify-content-center ${styles['icon-background']}`}
                style={{
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-font)',
                  marginLeft: 3,
                  width: 27,
                  height: 27,
                }}
              >
                <IconContext.Provider
                  value={{
                    color: 'var(--color-font)',
                    size: 12,
                  }}
                >
                  <FiExternalLink />
                </IconContext.Provider>
              </div>
            </div>
            <div style={{ marginLeft: 5 }}>Overwatch Community</div>
          </div>
        </Button>
        {/* User Menu Dropdown */}
        <Popup
          on="hover"
          mouseLeaveDelay={300}
          trigger={
            <Button
              className={`me-3 gap-2 justify-content-between ${styles.pill} ${styles['user-menu-trigger']}`}
              variant="primary"
              aria-label="Open user menu"
            >
              <span
                className={`d-flex align-items-center justify-content-center ${styles['user-menu-trigger-icon']}`}
              >
                <FaRegUser size={14} />
              </span>
              <span className="flex-grow-1 text-start">User Menu</span>
              <FaChevronDown size={15} aria-hidden="true" />
            </Button>
          }
          position="bottom right"
          closeOnDocumentClick
          closeOnEscape
          arrow={false}
          contentStyle={{
            width: USER_MENU_WIDTH,
            padding: 0,
            background: 'transparent',
            border: 0,
          }}
        >
          <nav
            className={`d-flex flex-column p-2 ${styles['user-menu-dropdown']}`}
            aria-label="User menu"
          >
            <div
              className={`d-flex align-items-center gap-2 px-2 py-2 mb-1 ${styles['user-menu-summary']}`}
            >
              <FaRegUserCircle size={24} className="flex-shrink-0" />
              <div className="min-w-0 flex-grow-1">
                <div className={`text-theme-muted ${styles['user-menu-summary-label']}`}>
                  Signed in as
                </div>
                <div className={`text-truncate ${styles['user-menu-username']}`} title={username}>
                  {username}
                </div>
              </div>
            </div>
            <Link className={styles['user-menu-item']} to="/usermenu?tab=Account">
              <FaUser size={18} />
              <span>Edit Account</span>
            </Link>
            <Link className={styles['user-menu-item']} to="/usermenu?tab=Accessibility">
              <PiPersonArmsSpreadFill size={20} />
              <span>Accessibility Settings</span>
            </Link>
            <Link className={styles['user-menu-item']} to="/usermenu?tab=About">
              <IoInformationCircle size={20} />
              <span>About</span>
            </Link>
            <button className={styles['user-menu-item']} type="button" onClick={handleLogout}>
              <FiLogOut size={19} />
              <span>Logout</span>
            </button>
          </nav>
        </Popup>
      </div>
    </Container>
  );
}
